'use client';
import React from 'react';
import LazyLoad from 'react-lazyload';
import { useTranslation } from 'react-i18next';
import { Button, SectionTitle, Tabs, TabsInterface } from '@benzinga/core-ui';
import styles from './QuoteNewsSection.module.scss';
import { QuoteNewsFeed } from './QuoteNewsFeed';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import type { SimpleNewsQueryAndOptions } from '@benzinga/internal-news-manager';
import type { PressContent } from '../../entities/newsEntity';
import { OVERVIEW_RECENT_NEWS_LIMIT } from '../../utils/quote/tab-data/overview-recent-news';

const TaboolaPlacement = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.TaboolaPlacement })),
);

export interface QuoteNewsSectionProps {
  news: StoryObject[];
  query: SimpleNewsQueryAndOptions;
  companyName: string;
  variant?: 'stock' | 'crypto';
  pressRelease?: PressContent | null;
  belowNewsSection?: React.ReactNode;
}

const TaboolaQuotePlacement = () => (
  <LazyLoad className="min-h-[360px] md:min-h-[342px]" offset={100} once>
    <React.Suspense fallback={<div />}>
      <TaboolaPlacement
        container="taboola-mid-page-thumbnails---quotes"
        mode="thumbnails-mq"
        placement="Mid-Page Thumbnails - Quotes"
        settings={{ other: 'auto' }}
        url=""
        vizSensor={false}
      />
    </React.Suspense>
  </LazyLoad>
);

const QuoteNewsSection = ({
  news,
  query,
  companyName,
  variant = 'stock',
  pressRelease,
  belowNewsSection,
}: QuoteNewsSectionProps) => {
  const { t, i18n } = useTranslation('quote');
  const [newsTab, setNewsTab] = React.useState('news');
  const isGlobalSite = i18n.language !== 'en';
  const isCrypto = variant === 'crypto';

  const newsFeed = isCrypto ? (
    <QuoteNewsFeed
      nodes={news || []}
      query={query}
      seeMoreEnabled={true}
      seeMoreLabel={t('Buttons.see-more')}
      showLoadMoreButton={true}
    />
  ) : (
    <QuoteNewsFeed
      adUnit={<TaboolaQuotePlacement />}
      nodes={news ?? []}
      query={query}
      seeMoreEnabled={(news?.length ?? 0) >= OVERVIEW_RECENT_NEWS_LIMIT}
      seeMoreLabel={t('Buttons.read-more')}
    />
  );

  if (isCrypto) {
    return (
      <div className={`${styles.cryptoContainer} news-tab-and-ads-wrapper w-full flex flex-col lg:flex-row gap-8 md:gap-4`}>
        <div className="w-full">
          <div className="mb-4">
            <SectionTitle className="leading-none" level={3}>
              {t('Quote.News.recent-news', { companyName })}
            </SectionTitle>
          </div>
          {newsFeed}
        </div>
      </div>
    );
  }

  const tabs: TabsInterface[] = [
    { component: newsFeed, key: 'news', name: t('Quote.News.news') },
    {
      component: (
        <>
          <QuoteNewsFeed nodes={pressRelease?.sortedContent ?? []} seeMoreLabel={t('Buttons.read-more')} />
          <Button
            as="a"
            className="font-bold inline-block w-full mt-4"
            href="https://pro.benzinga.com/?utm_source=benzinga.com&utm_campaign=quote-press-release"
            target="_blank"
            variant="flat-blue"
          >
            {t('Quote.News.press-release-full-archive')}
          </Button>
        </>
      ),
      key: 'press_release',
      name: t('Quote.News.press-release'),
    },
  ];

  return (
    <div className="news-tab-and-ads-wrapper w-full flex flex-col lg:flex-row gap-8 md:gap-4">
      <div className={`${styles.styledTabsWrapper} w-full`}>
        <div className="flex items-center mb-2 recent-news-title">
          <SectionTitle className="leading-1.5" level={2}>
            {t('Quote.News.recent-news', { companyName })}
          </SectionTitle>
        </div>
        {isGlobalSite ? (
          newsFeed
        ) : (
          <Tabs
            activeTab={newsTab}
            enableLazyLoad
            isShowMoreButtonEnabled={false}
            showScrollShadow={false}
            onChange={setNewsTab}
            tabs={tabs}
            variant="pill"
          />
        )}
        {belowNewsSection}
      </div>
    </div>
  );
};

export default QuoteNewsSection;
